import {createSlice} from '@reduxjs/toolkit';

import channelTypes from '../../consts/channelTypes';
import {LOADER_TYPES} from '../../consts/loader/loaderTypes';
import storeSliceNames from '../../consts/storeSliceNames';
import themeStyle from '../../consts/themeStyle';
import storageService from '../../services/storageService';
import {userBrowserService} from '../../services/userBrowserService';
import helpers from '../../utils/helpers';

const initialState = {
    notifications: [],
    isAppInitialized: false,
    isOnline: navigator.onLine,
    isNetworkNotificationsDisabled: false,
    isAgeGateConfirmed: storageService.getAgeGateFromStorage(),
    isGetStartedPageShown: storageService.getIsGetStartedPageShown(),
    isPWA: userBrowserService.getIsPWA(),
    isPWAInstalled: false,
    swMessage: undefined,
    globalLoaders: [],
    theme: themeStyle.DARK, // :themeStyle.LIGHT, (Light mode disabled — always dark)
    overriddenTheme: undefined,
    isUseSystemTheme: false, // :true, (Light mode disabled — always dark)
    channelType: channelTypes.CONSUMER,
    isDevMockSession: false,
};

const globalSlice = createSlice({
    name: storeSliceNames.global,
    initialState,
    reducers: {
        setNotification: (state, action) => {
            const notificationId = action.payload?.id || helpers.guid();
            const isNotificationExists = state.notifications.some((n) => n.id === notificationId);

            if (isNotificationExists) return;

            state.notifications.push({id: notificationId, ...action.payload});
        },
        removeNotification: (state, action) => {
            state.notifications = state.notifications.filter((n) => n.id !== action.payload);
        },
        clearDeviceNotifications: (state, action) => {
            state.notifications = state.notifications.filter((n) => n.dusn !== action.payload);
        },
        clearNotificationsByType: (state, action) => {
            state.notifications = state.notifications.filter((n) => n.type !== action.payload);
        },
        clearNotifications: (state) => {
            state.notifications = initialState.notifications;
        },
        setIsAppInitialized: (state, action) => {
            state.isAppInitialized = action.payload;
        },
        setIsOnline: (state, action) => {
            state.isOnline = action.payload;
        },
        setIsNetworkNotificationsDisabled: (state, action) => {
            state.isNetworkNotificationsDisabled = action.payload;
        },
        setIsAgeGateConfirmed: (state, action) => {
            state.isAgeGateConfirmed = action.payload;
        },
        setIsGetStartedPageShown: (state, action) => {
            state.isGetStartedPageShown = action.payload;
        },
        setIsPWA: (state, action) => {
            state.isPWA = action.payload;
        },
        setIsPWAInstalled: (state, action) => {
            state.isPWAInstalled = action.payload;
        },
        setSwMessage: (state, action) => {
            state.swMessage = action.payload;
        },
        showGlobalLoader: (state, action) => {
            const {type} = action.payload;

            const isLoaderExists = state.globalLoaders.some((loader) => loader.type === type);

            if (isLoaderExists) return state;

            return {
                ...state,
                globalLoaders: [...state.globalLoaders, action.payload],
            };
        },
        hideGlobalLoader: (state, action) => {
            const {type} = action.payload;

            return {
                ...state,
                globalLoaders: state.globalLoaders.filter((loader) => loader.type !== type),
            };
        },
        setTheme: (state, action) => {
            state.theme = action.payload;
        },
        setOverriddenTheme: (state, action) => {
            state.overriddenTheme = action.payload;
        },
        setIsUseSystemTheme: (state, action) => {
            state.isUseSystemTheme = action.payload;
        },
        setChannelType: (state, action) => {
            state.channelType = action.payload;
        },
        setIsDevMockSession: (state, action) => {
            state.isDevMockSession = action.payload;
        },
    },
});

export const setNotification = (notification) => globalSlice.actions.setNotification(notification);

export const removeNotification = (notification) => globalSlice.actions.removeNotification(notification);

export const clearDeviceNotifications = (dusn) => globalSlice.actions.clearDeviceNotifications(dusn);

export const clearNotificationsByType = (dusn) => globalSlice.actions.clearNotificationsByType(dusn);

export const clearNotifications = () => globalSlice.actions.clearNotifications();

export const setIsAppInitialized = (isConfigured) => globalSlice.actions.setIsAppInitialized(isConfigured);

export const setIsOnline = (isOnline) => globalSlice.actions.setIsOnline(isOnline);

export const setIsNetworkNotificationsDisabled = (isDisabled) =>
    globalSlice.actions.setIsNetworkNotificationsDisabled(isDisabled);

export const setAgeGateConfirmed = (isAgeGate) => globalSlice.actions.setIsAgeGateConfirmed(isAgeGate);

export const setIsGetStartedPageShown = (isGetStartedPageShown) =>
    globalSlice.actions.setIsGetStartedPageShown(isGetStartedPageShown);

export const setIsPWA = (isPWA) => globalSlice.actions.setIsPWA(isPWA);

export const setIsPWAInstalled = (isPWAInstalled) => globalSlice.actions.setIsPWAInstalled(isPWAInstalled);

export const setSwMessage = (swMessage) => globalSlice.actions.setSwMessage(swMessage);

export const showGlobalLoader = (props = {}) => {
    const {type = LOADER_TYPES.DEFAULT_LOADER, ...rest} = props;

    return globalSlice.actions.showGlobalLoader({type, ...rest});
};

export const hideGlobalLoader = (props = {}) => {
    const {type = LOADER_TYPES.DEFAULT_LOADER, ...rest} = props;

    return globalSlice.actions.hideGlobalLoader({type, ...rest});
};

export const setTheme = (theme) => globalSlice.actions.setTheme(theme);

export const setOverriddenTheme = (theme) => globalSlice.actions.setOverriddenTheme(theme);

export const setIsUseSystemTheme = (isUseSystemTheme) => globalSlice.actions.setIsUseSystemTheme(isUseSystemTheme);

export const setChannelType = (channelType) => globalSlice.actions.setChannelType(channelType);

export const setIsDevMockSession = (isDevMockSession) => globalSlice.actions.setIsDevMockSession(isDevMockSession);

export default globalSlice.reducer;
